'use client';

import { usePathname } from 'next/navigation';
import { FC, useEffect, useState } from 'react';

import { initCarmaEventTracker, pushCarmaEvent, removeCarmaEventTracker } from 'src/general/helpers/carmaCritical';
import { pushToDataLayer } from 'src/utils/pushToDataLayer';

import { handleTrackingCookies } from './Analytics.helpers';

/**
 * Analytics component for client-side tracking and analytics integration.
 *
 * Responsibilities:
 * - Initializes Carma event tracker and ensures tracking cookies are set on mount.
 * - Pushes navigation events to both Carma and GTM data layer when the route changes and tracking cookies are set.
 *
 * This component should be placed near the root of the app.
 */
export const Analytics: FC = () => {
  const pathname = usePathname();
  const [isTrackingCookiesSet, setIsTrackingCookiesSet] = useState<boolean>(false);

  useEffect(() => {
    let isMounted = true;
    initCarmaEventTracker();
    handleTrackingCookies().then(() => {
      if (isMounted) {
        setIsTrackingCookiesSet(true);
      }
    });

    return () => {
      isMounted = false;
      removeCarmaEventTracker();
    };
  }, []);

  useEffect(() => {
    if (!isTrackingCookiesSet) return;
    pushToDataLayer({ event: 'navigation', pathname: pathname ?? '/' });
    pushCarmaEvent({ event: 'navigation' });
  }, [pathname, isTrackingCookiesSet]);

  return null;
};
